import React, { useState, useEffect } from "react";
import { complianceAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export const CompliancePage = () => {
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFramework, setActiveFramework] = useState("all");

  useEffect(() => {
    fetchCompliance();
  }, []);

  const fetchCompliance = async () => {
    try {
      const response = await complianceAPI.getAll();
      setCompliance(response.items);
    } catch (err) {
      toast.error("Failed to fetch compliance data");
    } finally {
      setLoading(false);
    }
  };

  const frameworks = ["all", ...new Set(compliance.map((c) => c.framework))];

  const filteredCompliance =
    activeFramework === "all"
      ? compliance
      : compliance.filter((c) => c.framework === activeFramework);

  const getStatusIcon = (status) => {
    switch (status) {
      case "passed":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "passed":
        return "bg-green-100 text-green-800 border-green-200";
      case "failed":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-amber-100 text-amber-800 border-amber-200";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Calculate stats for each framework
  const getFrameworkStats = (framework) => {
    const items =
      framework === "all"
        ? compliance
        : compliance.filter((c) => c.framework === framework);
    const passed = items.filter((c) => c.status === "passed").length;
    const failed = items.filter((c) => c.status === "failed").length;
    const warning = items.filter((c) => c.status === "warning").length;
    const score =
      items.length > 0 ? Math.round((passed / items.length) * 100) : 0;
    return { passed, failed, warning, total: items.length, score };
  };

  const overallStats = getFrameworkStats("all");

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Framework",
      "Requirement",
      "Status",
      "Last Checked",
      "Evidence",
    ];
    const rows = filteredCompliance.map((item) => [
      item.framework,
      item.requirement,
      item.status,
      formatDate(item.last_checked),
      item.evidence || "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `compliance_report_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("CSV report downloaded!");
  };

  // Export to PDF
  const exportToPDF = () => {
    const stats = getFrameworkStats(activeFramework);
    const reportDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const reportTime = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const frameworkLabel = activeFramework === "all" ? "All Frameworks" : activeFramework;

    // Score ring SVG
    const scoreColor = stats.score >= 80 ? "#059669" : stats.score >= 50 ? "#d97706" : "#dc2626";
    const circumference = 2 * Math.PI * 54;
    const strokeDashoffset = circumference - (stats.score / 100) * circumference;

    // Group items by framework for multi-framework reports
    const groupedByFramework = {};
    filteredCompliance.forEach((item) => {
      if (!groupedByFramework[item.framework]) groupedByFramework[item.framework] = [];
      groupedByFramework[item.framework].push(item);
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>SecureSight Compliance Report - ${frameworkLabel}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #1e293b;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          @page {
            size: A4;
            margin: 0;
          }

          /* ---- Cover / Header ---- */
          .cover {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
            color: #fff;
            padding: 60px 56px 48px;
            position: relative;
            overflow: hidden;
          }
          .cover::after {
            content: '';
            position: absolute;
            top: -80px; right: -80px;
            width: 320px; height: 320px;
            border-radius: 50%;
            background: rgba(99, 102, 241, 0.08);
          }
          .cover-brand {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 32px;
          }
          .cover-brand svg { flex-shrink: 0; }
          .cover-brand span {
            font-size: 22px;
            font-weight: 700;
            letter-spacing: -0.5px;
          }
          .cover h1 {
            font-size: 32px;
            font-weight: 700;
            letter-spacing: -0.5px;
            margin-bottom: 6px;
          }
          .cover-sub {
            font-size: 15px;
            color: #94a3b8;
          }
          .cover-meta {
            display: flex;
            gap: 32px;
            margin-top: 28px;
          }
          .cover-meta div { font-size: 13px; color: #cbd5e1; }
          .cover-meta strong { color: #fff; font-weight: 600; }

          /* ---- Page body ---- */
          .page-body { padding: 40px 56px 48px; }

          /* ---- Score banner ---- */
          .score-banner {
            display: flex;
            align-items: center;
            gap: 40px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 32px 36px;
            margin-bottom: 36px;
          }
          .score-ring { flex-shrink: 0; }
          .score-ring text {
            font-family: 'Inter', sans-serif;
          }
          .kpi-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
            flex: 1;
          }
          .kpi { text-align: center; }
          .kpi-value {
            font-size: 28px;
            font-weight: 700;
            line-height: 1;
          }
          .kpi-label {
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #64748b;
            margin-top: 6px;
            font-weight: 500;
          }

          /* ---- Section headings ---- */
          .section-title {
            font-size: 16px;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e2e8f0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .framework-heading {
            font-size: 14px;
            font-weight: 600;
            color: #334155;
            margin: 24px 0 10px;
            padding: 8px 12px;
            background: #f1f5f9;
            border-left: 3px solid #6366f1;
            border-radius: 0 6px 6px 0;
          }

          /* ---- Table ---- */
          table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-size: 12px;
            margin-bottom: 8px;
          }
          thead th {
            background: #0f172a;
            color: #e2e8f0;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 11px;
            padding: 10px 14px;
            text-align: left;
          }
          thead th:first-child { border-radius: 8px 0 0 0; }
          thead th:last-child { border-radius: 0 8px 0 0; }
          tbody td {
            padding: 10px 14px;
            border-bottom: 1px solid #f1f5f9;
            vertical-align: top;
          }
          tbody tr:last-child td { border-bottom: none; }
          tbody tr:nth-child(even) { background: #f8fafc; }
          tbody tr:hover { background: #f1f5f9; }

          /* Status pills */
          .status-pill {
            display: inline-block;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            padding: 3px 10px;
            border-radius: 999px;
          }
          .status-passed { background: #dcfce7; color: #166534; }
          .status-failed { background: #fee2e2; color: #991b1b; }
          .status-warning { background: #fef3c7; color: #92400e; }

          /* ---- Progress bar (score breakdown) ---- */
          .progress-row {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 6px;
            font-size: 12px;
          }
          .progress-label {
            width: 60px;
            font-weight: 500;
            color: #475569;
          }
          .progress-track {
            flex: 1;
            height: 8px;
            background: #e2e8f0;
            border-radius: 4px;
            overflow: hidden;
          }
          .progress-fill {
            height: 100%;
            border-radius: 4px;
            transition: width 0.3s;
          }
          .progress-value {
            width: 36px;
            text-align: right;
            font-weight: 600;
            font-size: 12px;
            color: #334155;
          }

          /* ---- Footer ---- */
          .report-footer {
            margin-top: 48px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: #94a3b8;
            font-size: 11px;
          }
          .report-footer .confidential {
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 600;
            color: #cbd5e1;
          }

          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .cover { break-after: avoid; }
            table { break-inside: auto; }
            tr { break-inside: avoid; }
            .page-body { padding: 32px 48px; }
          }
        </style>
      </head>
      <body>
        <!-- Cover Header -->
        <div class="cover">
          <div class="cover-brand">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <rect width="36" height="36" rx="8" fill="#6366f1"/>
              <path d="M18 8L26 12V20C26 24.4 22.5 28.4 18 29C13.5 28.4 10 24.4 10 20V12L18 8Z" fill="white" fill-opacity="0.9"/>
              <path d="M16 18.5L17.5 20L20.5 16" stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>SecureSight</span>
          </div>
          <h1>Compliance Report</h1>
          <p class="cover-sub">${frameworkLabel}</p>
          <div class="cover-meta">
            <div><strong>Date:</strong> ${reportDate}</div>
            <div><strong>Time:</strong> ${reportTime}</div>
            <div><strong>Items Assessed:</strong> ${stats.total}</div>
          </div>
        </div>

        <div class="page-body">
          <!-- Score Banner -->
          <div class="score-banner">
            <svg class="score-ring" width="130" height="130" viewBox="0 0 130 130">
              <circle cx="65" cy="65" r="54" fill="none" stroke="#e2e8f0" stroke-width="10"/>
              <circle cx="65" cy="65" r="54" fill="none" stroke="${scoreColor}" stroke-width="10"
                stroke-linecap="round" stroke-dasharray="${circumference}"
                stroke-dashoffset="${strokeDashoffset}"
                transform="rotate(-90 65 65)"/>
              <text x="65" y="60" text-anchor="middle" font-size="28" font-weight="700" fill="${scoreColor}">${stats.score}%</text>
              <text x="65" y="78" text-anchor="middle" font-size="10" fill="#94a3b8" font-weight="500">SCORE</text>
            </svg>
            <div class="kpi-grid">
              <div class="kpi">
                <div class="kpi-value" style="color:#059669;">${stats.passed}</div>
                <div class="kpi-label">Passed</div>
              </div>
              <div class="kpi">
                <div class="kpi-value" style="color:#dc2626;">${stats.failed}</div>
                <div class="kpi-label">Failed</div>
              </div>
              <div class="kpi">
                <div class="kpi-value" style="color:#d97706;">${stats.warning}</div>
                <div class="kpi-label">Warnings</div>
              </div>
            </div>
          </div>

          <!-- Score breakdown bar -->
          <div style="margin-bottom:36px;">
            <div class="progress-row">
              <span class="progress-label">Passed</span>
              <div class="progress-track">
                <div class="progress-fill" style="width:${stats.total ? (stats.passed / stats.total) * 100 : 0}%; background:#059669;"></div>
              </div>
              <span class="progress-value">${stats.passed}</span>
            </div>
            <div class="progress-row">
              <span class="progress-label">Failed</span>
              <div class="progress-track">
                <div class="progress-fill" style="width:${stats.total ? (stats.failed / stats.total) * 100 : 0}%; background:#dc2626;"></div>
              </div>
              <span class="progress-value">${stats.failed}</span>
            </div>
            <div class="progress-row">
              <span class="progress-label">Warning</span>
              <div class="progress-track">
                <div class="progress-fill" style="width:${stats.total ? (stats.warning / stats.total) * 100 : 0}%; background:#d97706;"></div>
              </div>
              <span class="progress-value">${stats.warning}</span>
            </div>
          </div>

          <!-- Requirements Table -->
          <div class="section-title">Detailed Requirements</div>
          ${Object.entries(groupedByFramework).map(([fw, items]) => `
            ${Object.keys(groupedByFramework).length > 1 ? `<div class="framework-heading">${fw}</div>` : ""}
            <table>
              <thead>
                <tr>
                  ${Object.keys(groupedByFramework).length <= 1 ? '<th style="width:20%;">Framework</th>' : ""}
                  <th>Requirement</th>
                  <th style="width:100px;">Status</th>
                  <th style="width:120px;">Last Checked</th>
                </tr>
              </thead>
              <tbody>
                ${items.map((item) => `
                  <tr>
                    ${Object.keys(groupedByFramework).length <= 1 ? `<td>${item.framework}</td>` : ""}
                    <td>${item.requirement}</td>
                    <td><span class="status-pill status-${item.status}">${item.status}</span></td>
                    <td>${formatDate(item.last_checked)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `).join("")}

          <!-- Footer -->
          <div class="report-footer">
            <span class="confidential">Confidential</span>
            <span>SecureSight Security Platform &middot; Generated ${reportDate}</span>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success("PDF report opened for printing!");
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="compliance-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            Compliance
          </h1>
          <p className="text-muted-foreground mt-1">
            Track regulatory compliance across frameworks
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-report-btn">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={exportToCSV}
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={exportToPDF}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Overall Score Card */}
      <Card
        className="border-border shadow-sm"
        data-testid="overall-compliance-card"
      >
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="#E2E8F0"
                      strokeWidth="12"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke={
                        overallStats.score >= 80
                          ? "#10B981"
                          : overallStats.score >= 60
                            ? "#F59E0B"
                            : "#EF4444"
                      }
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={`${overallStats.score * 3.52} 352`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-heading text-3xl font-bold">
                      {overallStats.score}%
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Overall Score
                </p>
              </div>
            </div>
            <div className="md:col-span-3">
              <div className="grid grid-cols-3 gap-4 h-full items-center">
                <div className="text-center p-4 rounded-lg bg-green-50">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-heading text-2xl font-bold text-green-600">
                      {overallStats.passed}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Passed</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-amber-50">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-heading text-2xl font-bold text-amber-600">
                      {overallStats.warning}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Warning</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-red-50">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-heading text-2xl font-bold text-red-600">
                      {overallStats.failed}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Failed</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Framework Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {frameworks
          .filter((f) => f !== "all")
          .map((framework) => {
            const stats = getFrameworkStats(framework);
            return (
              <Card
                key={framework}
                className={`border-border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                  activeFramework === framework ? "ring-2 ring-primary" : ""
                }`}
                onClick={() => setActiveFramework(framework)}
                data-testid={`framework-card-${framework.toLowerCase().replace(" ", "-")}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 rounded-md bg-primary/10">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {stats.total} requirements
                    </Badge>
                  </div>
                  <h3 className="font-heading font-semibold mb-2">
                    {framework}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Compliance</span>
                      <span className="font-medium">{stats.score}%</span>
                    </div>
                    <Progress value={stats.score} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>

      {/* Compliance Items */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-semibold">
              Compliance Requirements
            </CardTitle>
            <Tabs value={activeFramework} onValueChange={setActiveFramework}>
              <TabsList>
                {frameworks.map((framework) => (
                  <TabsTrigger
                    key={framework}
                    value={framework}
                    className="capitalize"
                    data-testid={`tab-${framework.toLowerCase().replace(" ", "-")}`}
                  >
                    {framework}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Requirement</TableHead>
                  <TableHead>Framework</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Evidence</TableHead>
                  <TableHead>Last Checked</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompliance.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No compliance items found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompliance.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`compliance-row-${item.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getStatusIcon(item.status)}
                          <span className="font-medium">
                            {item.requirement}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{item.framework}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getStatusBadge(item.status)} capitalize`}
                        >
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.evidence ? (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <FileText className="w-3 h-3" />
                            {item.evidence}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            No evidence
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.last_checked)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
